import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';

import Modal from '@components/common/Modal';
import Table from '@components/common/Table';
import AddNewAssetToRequest from '@components/routes/AdminPanel/AssetsRent/RequestReservationCard/AddNewAssetToRequest';
import RequestActionConfirmationModal
	from '@components/routes/AdminPanel/AssetsRent/RequestReservationCard/RequestActionConfirmationModal';

import { useMutateAcceptOneRentAsset } from '@hooks/assets/useMutateAcceptOneRentAsset';
import { useMutateAddItemToExistingAssetRentRequest } from '@hooks/assets/useMutateAddItemToExistingAssetRentRequest';
import { useMutateDeclineOneRentAsset } from '@hooks/assets/useMutateDeclineOneRentAsset';
import { useMutateRemoveProposedAssetApi } from '@hooks/assets/useMutateRemoveProposedAssetApi';
import { useQueryAssetRentCardItems } from '@hooks/assets/useQueryAssetRentCardItems';
import { useQueryAssetTypes } from '@hooks/assets/useQueryAssetTypes';

import { auth } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import { setSnackbar } from '@slices/common';
import { viewOnMap } from '@slices/map';

import { TSelectOption } from '@interfaces/components/componentTypes';
import {
	ISortBy,
	ITableDatepickerFilter,
	ITablePagination,
	ITableSearch,
	ITableSort,
	TTableFilter,
} from '@interfaces/components/table';
import { IAssetRentRequest, IAssetRentRequestCardItem } from '@interfaces/rent/assetRent';
import { IAddItemToExistingAssetRentRequestDTO } from '@interfaces/rent/dto/assetRentDTO';

import { ECommonRequestStatusesForAssetRequest } from '@enums/asset';
import { defaultMeta, ESnackbarTypes } from '@enums/common';

import { responseMessages } from '@constants/responseMessages';
import { routes } from '@constants/routes';
import { addNewTooltipText, ERoles } from '../constants';
import { acquiredAssetProfilePath } from '@components/routes/AdminPanel/ManageAcquiredAssets/constants';

import { actionButtons, requestApprovingSteps } from './../StepperWithManaging/utils';
import ApprovalUnitsModal from '../ApprovalUnitsModal';
import StepperWithManaging from '../StepperWithManaging';
import { getFilters } from './getFilters';
import { getColumns } from './manageRequestsColumns';

import styles from '../RequestReservationCard.module.scss';

const { map } = routes;

const {
	accepted,
	cancelled,
	contract,
	declined,
	done,
	ended,
	sentForAcceptance,
} = ECommonRequestStatusesForAssetRequest;

export const removeConfirmationQuestion = 'Are you sure you want to delete proposed asset from the request?';
export const revokeDatesConfirmationQuestion = 'Are you sure you want to revoke date changes?';

export interface IRequestTableProps {
	acceptAssetRentRequest: () => void;
	contractAssetRentRequest: () => void;
	doneAssetRentRequest: () => void;
	openProposeDatesModal: (selectedItem: TSelectOption) => void;
	reFetchRequestCard: () => void;
	request: IAssetRentRequest;
	role: string;
	sendRequestToAO: () => void;
	sendRequestToMNO: () => void;
}

const RequestTable = ({
	acceptAssetRentRequest,
	contractAssetRentRequest,
	doneAssetRentRequest,
	openProposeDatesModal,
	reFetchRequestCard,
	request,
	role,
	sendRequestToAO,
	sendRequestToMNO,
}: IRequestTableProps) => {
	const { accessPermission } = useAppSelector(auth);

	const { assetOwnerAttributeUpdater, assetOwnerManageRequests } = accessPermission;

	const [page, setPage] = useState<number>(defaultMeta.page);
	const [pageSize, setPageSize] = useState<number>(defaultMeta.pageSize);
	const [startRentDate, setStartRentDate] = useState<number | undefined | null>(null);
	const [endRentDate, setEndRentDate] = useState<number | undefined | null>(null);
	const [searchValue, setSearchValue] = useState<string>('');
	const [sortBy, setSortBy] = useState<{ sortDirection: boolean; sortFieldName: string }>({
		sortDirection: true,
		sortFieldName: '',
	});

	const onChangeStartRentDate = useCallback((value?: number | null) => setStartRentDate(value), []);

	const onChangeEndRentDate = useCallback((value?: number | null) => setEndRentDate(value), []);

	const isEditAccess = assetOwnerManageRequests || assetOwnerAttributeUpdater;
	const isAO = role === ERoles.ao;

	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	const [selectedAssetType, setSelectedAssetType] = useState<TSelectOption<string>>(null);
	const [selectedApprovalStatus, setSelectedApprovalStatus] = useState<TSelectOption<string>>(null);
	const [isAddAssetModalOpen, setIsAssetModalOpen] = useState<boolean>(false);

	const openAddAssetModal = useCallback(() => setIsAssetModalOpen(true), []);
	const closeAddAssetModal = useCallback(() => {
		setIsAssetModalOpen(false);
	}, []);

	const { assetTypesSubtypes } = useQueryAssetTypes({ isAssetTypesSubtypesWithIconUrl: false });

	const reFetchRequestCardHandler = useCallback(() => reFetchRequestCard(), [reFetchRequestCard]);

	const {
		assetItems,
		countOfRecords,
		isLoading,
		reFetchItems,
	} = useQueryAssetRentCardItems({
		id: request.id,
		role,
		params: {
			approveStatus: selectedApprovalStatus?.name,
			typeId: selectedAssetType?.value,
			endRentDate,
			page,
			pageSize,
			searchValue,
			sortDirection: sortBy.sortDirection,
			sortFieldName: sortBy.sortFieldName,
			startRentDate,
		},
	});

	const { removeProposedAsset } = useMutateRemoveProposedAssetApi(
		request.id,
		[reFetchRequestCardHandler, reFetchItems],
	);

	const { addNewAssetToRequest } = useMutateAddItemToExistingAssetRentRequest(
		request.id,
		[reFetchRequestCardHandler, reFetchItems],
	);

	const { acceptProposedAsset } = useMutateAcceptOneRentAsset(
		request.id,
		[reFetchRequestCardHandler, reFetchItems],
	);

	const { declineProposedAsset } = useMutateDeclineOneRentAsset(
		request.id,
		[reFetchRequestCardHandler, reFetchItems],
	);

	const [isRemoveProposedAssetModal, setIsRemoveProposedAssetModal] = useState(false);
	const [removableAssetId, setRemovableAssetId] = useState<string | null>(null);

	const openRemoveProposedAssetModal = (id: string) => {
		setRemovableAssetId(id);
		setIsRemoveProposedAssetModal(true);
	};

	const closeRemoveProposedAssetModal = () => {
		setRemovableAssetId(null);
		setIsRemoveProposedAssetModal(false);
	};

	const handleRemoveProposedAsset = () => {
		if (removableAssetId) removeProposedAsset({ assetId: removableAssetId });

		closeRemoveProposedAssetModal();
	};

	const showOnTheMap: () => void = useCallback(() => {
		navigate(map.index, { state: { isRedirectedFromAdminPanel: true } });
	}, [navigate]);

	const handleViewOnMap = useCallback(async (assetId: string) => {
		try {
			await dispatch(viewOnMap({ assetId }));

			showOnTheMap();
		} catch (err) {
			dispatch(setSnackbar({
				message: (err as Error)?.message || responseMessages.error,
				type: ESnackbarTypes.error,
			}));
		}
	}, [dispatch, showOnTheMap]);

	const [isOpenApprovalInfo, setIsOpenApprovalInfo] = useState(false);
	const [asset, setAsset] = useState<IAssetRentRequestCardItem>();

	const openApprovalInfo = (asset: IAssetRentRequestCardItem) => {
		setIsOpenApprovalInfo(true);
		setAsset(asset);
	};

	const openProfileHandler = useCallback((assetDbId: string) => {
		navigate(`${acquiredAssetProfilePath}/${assetDbId}`);
	}, [navigate]);

	useEffect(() => {
		if (asset) {
			setAsset(assetItems.find((item: IAssetRentRequestCardItem) => item.id === asset.id));
		}
	}, [asset, assetItems]);

	const closeApprovalInfo = () => {
		setIsOpenApprovalInfo(false);
		setAsset(undefined);
	};

	const columns: Array<ColumnDef<any>> = useMemo(() => {
		return getColumns({
			acceptProposedAsset,
			declineProposedAsset,
			handleOpenApprovalInfo: openApprovalInfo,
			handleViewOnMap,
			isAO,
			openProfileHandler,
			openProposeDatesModal,
			removeProposedAsset: openRemoveProposedAssetModal,
			request,
			isAssetItemsLoading: isLoading,
		});
	}, [
		acceptProposedAsset,
		declineProposedAsset,
		handleViewOnMap,
		isAO,
		openProfileHandler,
		openProposeDatesModal,
		request,
		isLoading,
	]);

	const pageHandler = useCallback((page: number): void => setPage(page), []);

	const pageSizeHandler = useCallback((pageSize: number): void => {
		setPageSize(pageSize);
		setPage(1);
	}, []);

	const handleSort = useCallback((sortBy: Array<ISortBy>): void => {
		setSortBy({
			sortDirection: !!sortBy[0]?.desc,
			sortFieldName: sortBy[0]?.id,
		});
	}, []);

	const sort: ITableSort = useMemo(() => {
		return {
			initialState: [{ id: sortBy.sortFieldName, desc: sortBy.sortDirection }],
			handleSort,
		};
	}, [handleSort, sortBy.sortDirection, sortBy.sortFieldName]);

	const onSearchHandler = useCallback((searchValue: string): void => {
		setPage(1);
		setSearchValue(searchValue);
	}, []);

	const search: Array<ITableSearch> = useMemo(() => {
		return [
			{
				initialValue: searchValue,
				name: 'searchValue',
				handler: onSearchHandler,
				tooltipText: 'Search by asset ID',
			},
		];
	}, [onSearchHandler, searchValue]);

	const filters: Array<TTableFilter> = useMemo(() => {
		return getFilters({
			assetTypes: assetTypesSubtypes,
			selectedApprovalStatus,
			selectedAssetType,
			setSelectedApprovalStatus,
			setSelectedAssetType,
		});
	}, [selectedAssetType, selectedApprovalStatus, assetTypesSubtypes]);

	const dateFilters: Array<ITableDatepickerFilter> = useMemo(() => {
		return [
			{
				name: 'startRentDate',
				label: 'Start rent date',
				handleChange: onChangeStartRentDate,
				selected: startRentDate ? new Date(startRentDate) : null,
			},
			{
				name: 'endRentDate',
				label: 'End rent date',
				handleChange: onChangeEndRentDate,
				selected: endRentDate ? new Date(endRentDate) : null,
			},
		];
	}, [startRentDate, endRentDate, onChangeStartRentDate, onChangeEndRentDate]);

	const pagination: ITablePagination = useMemo(() => {
		return {
			countOfRecords,
			forcePage: page,
			size: pageSize,
			pageHandler,
			pageSizeHandler,
		};
	}, [countOfRecords, page, pageHandler, pageSize, pageSizeHandler]);

	const requestStatus = request.requestStatus.name;

	const isAddNewAsset = isAO && assetOwnerManageRequests
		&& requestStatus !== sentForAcceptance
		&& requestStatus !== contract
		&& requestStatus !== done
		&& requestStatus !== declined
		&& requestStatus !== accepted
		&& requestStatus !== cancelled
		&& requestStatus !== ended;

	const actionStatusButtons = useMemo(() => {
		return actionButtons({
			activeStatus: requestStatus,
			isAssetOwner: isAO,
			accessPermission,
			actions: {
				acceptHandler: acceptAssetRentRequest,
				contractHandler: contractAssetRentRequest,
				doneHandler: doneAssetRentRequest,
				sendToAOHandler: sendRequestToAO,
				sendToMNOHandler: sendRequestToMNO,
			},
		});
	}, [
		acceptAssetRentRequest,
		accessPermission,
		contractAssetRentRequest,
		doneAssetRentRequest,
		isAO,
		requestStatus,
		sendRequestToAO,
		sendRequestToMNO,
	]);

	const approvingSteps = useMemo(() => requestApprovingSteps(requestStatus), [requestStatus]);

	const handleAddNewAssetToRequest = useCallback((data: IAddItemToExistingAssetRentRequestDTO) => {
		void addNewAssetToRequest(data);
		closeAddAssetModal();
	}, [addNewAssetToRequest, closeAddAssetModal]);

	const isRemovableAssetWithChangeDates = useMemo(() => {
		return assetItems.find((item: IAssetRentRequestCardItem) => {
			return item?.id === removableAssetId;
		})?.proposalInfo?.type?.name === 'Change dates';
	}, [assetItems, removableAssetId]);

	return (
		<>
			<StepperWithManaging
				buttons={actionStatusButtons}
				steps={approvingSteps}
			/>
			<div className={styles.requestTableContainer}>
				<Modal
					isConfirmDialogNeededOnClose={false}
					isOpen={isAddAssetModalOpen}
					onClose={closeAddAssetModal}
					title='Propose asset'
					width='500px'
				>
					<AddNewAssetToRequest
						addNewAssetToRequest={handleAddNewAssetToRequest}
						assetItems={assetItems}
						closeModal={closeAddAssetModal}
						requestDbId={request.id}
					/>
				</Modal>
				<Table
					addTooltipText={addNewTooltipText}
					columns={columns}
					data={assetItems || []}
					dateFilters={dateFilters}
					filters={filters}
					isCreatable={isAddNewAsset}
					isEditAccess={isEditAccess}
					isLoading={isLoading}
					isOpenSearch={!!searchValue.length}
					isPinColumns
					isWithoutActions={false}
					isWithoutCheckbox
					isWithoutDeleteAction
					pagination={pagination}
					search={search}
					searchThreshold={0}
					sort={sort}
					stickyLeftColumnIndexes={[0, 1, 2]}
					toggleAddModal={openAddAssetModal}
				/>
			</div>
			{asset && (
				<Modal
					isOpen={isOpenApprovalInfo}
					maxWidth='1350px'
					onClose={closeApprovalInfo}
					title='APPROVALS'
					width='80%'
				>
					<ApprovalUnitsModal
						asset={asset}
						isAO={isAO}
						isRequestItemsLoading={isLoading}
						reFetchItems={reFetchItems}
						reFetchRequestCardHandler={reFetchRequestCardHandler}
						requestStatus={request.requestStatus.name}
					/>
				</Modal>
			)}
			<Modal
				isOpen={isRemoveProposedAssetModal}
				onClose={closeRemoveProposedAssetModal}
			>
				<RequestActionConfirmationModal
					acceptButtonTitle='Yes'
					cancelButtonTitle='No'
					closeModal={closeRemoveProposedAssetModal}
					description={
						isRemovableAssetWithChangeDates
							? removeConfirmationQuestion
							: revokeDatesConfirmationQuestion
					}
					isWithoutComments
					onApprove={handleRemoveProposedAsset}
				/>
			</Modal>
		</>
	);
};

export default memo(RequestTable);
